import type { Issue, PriorityId, StatusId, User } from "./types";

/** A stable URL-safe marker for a missing nullable value. */
export const NO_FILTER_VALUE = "__none__";

export type TaskHierarchyFilter = "top_level" | "subtask";
export type TaskVisibilityFilter = "private" | "public";

export type TaskDateFilter = {
  from: string | null;
  to: string | null;
  includeEmpty: boolean;
};

/**
 * Canonical task filter state.
 *
 * Facets are ANDed with one another. Values inside a facet are ORed. Nullable
 * facets use NO_FILTER_VALUE, so "Ruben or unassigned" remains expressible.
 */
export type TaskFilters = {
  assigneeIds: string[];
  ownerIds: string[];
  executorAgentIds: string[];
  requesterIds: string[];
  projectIds: string[];
  created: TaskDateFilter;
  teamIds: string[];
  statuses: StatusId[];
  priorities: PriorityId[];
  labelIds: string[];
  due: TaskDateFilter;
  updated: TaskDateFilter;
  completed: TaskDateFilter;
  authorIds: string[];
  cycleIds: string[];
  estimates: string[];
  hierarchy: TaskHierarchyFilter[];
  visibility: TaskVisibilityFilter[];
};

export type TaskFilterFacet = keyof TaskFilters;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const STATUS_VALUES = new Set<StatusId>([
  "backlog",
  "todo",
  "in_progress",
  "in_review",
  "done",
  "canceled",
]);
const PRIORITY_VALUES = new Set<PriorityId>([0, 1, 2, 3, 4]);
const HIERARCHY_VALUES = new Set<TaskHierarchyFilter>(["top_level", "subtask"]);
const VISIBILITY_VALUES = new Set<TaskVisibilityFilter>(["private", "public"]);

const QUERY_KEYS = {
  assigneeIds: "tf_assignee",
  ownerIds: "tf_owner",
  executorAgentIds: "tf_agent",
  requesterIds: "tf_reporter",
  projectIds: "tf_project",
  teamIds: "tf_team",
  statuses: "tf_status",
  priorities: "tf_priority",
  labelIds: "tf_label",
  authorIds: "tf_author",
  cycleIds: "tf_cycle",
  estimates: "tf_estimate",
  hierarchy: "tf_hierarchy",
  visibility: "tf_visibility",
  createdFrom: "tf_created_from",
  createdTo: "tf_created_to",
  dueFrom: "tf_due_from",
  dueTo: "tf_due_to",
  dueEmpty: "tf_due_empty",
  updatedFrom: "tf_updated_from",
  updatedTo: "tf_updated_to",
  completedFrom: "tf_completed_from",
  completedTo: "tf_completed_to",
  completedEmpty: "tf_completed_empty",
} as const;

export const TASK_FILTER_QUERY_KEYS = Object.freeze(Object.values(QUERY_KEYS));

export const TASK_NAV_QUERY_KEY = "task_nav";

const TASK_VIEW_QUERY_KEYS = Object.freeze([
  ...TASK_FILTER_QUERY_KEYS,
  "task_view",
  "task_team",
  "task_search",
  "task_layout",
  "task_scope_user",
  TASK_NAV_QUERY_KEY,
]);

function emptyDateFilter(): TaskDateFilter {
  return { from: null, to: null, includeEmpty: false };
}

export function createEmptyTaskFilters(): TaskFilters {
  return {
    assigneeIds: [],
    ownerIds: [],
    executorAgentIds: [],
    requesterIds: [],
    projectIds: [],
    created: emptyDateFilter(),
    teamIds: [],
    statuses: [],
    priorities: [],
    labelIds: [],
    due: emptyDateFilter(),
    updated: emptyDateFilter(),
    completed: emptyDateFilter(),
    authorIds: [],
    cycleIds: [],
    estimates: [],
    hierarchy: [],
    visibility: [],
  };
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}

function readList(params: URLSearchParams, key: string): string[] {
  const values = params
    .getAll(key)
    .flatMap((value) => value.split(","))
    .map((value) => value.trim())
    .filter((value) => value.length > 0 && value.length <= 120);
  return unique(values).slice(0, 100);
}

function validDate(value: string | null): string | null {
  if (!value || !DATE_RE.test(value)) return null;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day
    ? value
    : null;
}

function readDateFilter(
  params: URLSearchParams,
  keys: { from: string; to: string; empty?: string }
): TaskDateFilter {
  let from = validDate(params.get(keys.from));
  let to = validDate(params.get(keys.to));
  if (from && to && from > to) [from, to] = [to, from];
  return {
    from,
    to,
    includeEmpty: keys.empty ? params.get(keys.empty) === "1" : false,
  };
}

export function parseTaskFilters(params: URLSearchParams): TaskFilters {
  const statusValues = readList(params, QUERY_KEYS.statuses).filter(
    (value): value is StatusId => STATUS_VALUES.has(value as StatusId)
  );
  const priorityValues = readList(params, QUERY_KEYS.priorities)
    .map(Number)
    .filter((value): value is PriorityId => PRIORITY_VALUES.has(value as PriorityId));
  const estimates = readList(params, QUERY_KEYS.estimates).filter(
    (value) => value === NO_FILTER_VALUE || (/^\d+(\.\d+)?$/.test(value) && Number(value) <= 1000)
  );

  return {
    assigneeIds: readList(params, QUERY_KEYS.assigneeIds),
    ownerIds: readList(params, QUERY_KEYS.ownerIds),
    executorAgentIds: readList(params, QUERY_KEYS.executorAgentIds),
    requesterIds: readList(params, QUERY_KEYS.requesterIds),
    projectIds: readList(params, QUERY_KEYS.projectIds),
    created: readDateFilter(params, {
      from: QUERY_KEYS.createdFrom,
      to: QUERY_KEYS.createdTo,
    }),
    teamIds: readList(params, QUERY_KEYS.teamIds),
    statuses: unique(statusValues) as StatusId[],
    priorities: [...new Set(priorityValues)],
    labelIds: readList(params, QUERY_KEYS.labelIds),
    due: readDateFilter(params, {
      from: QUERY_KEYS.dueFrom,
      to: QUERY_KEYS.dueTo,
      empty: QUERY_KEYS.dueEmpty,
    }),
    updated: readDateFilter(params, {
      from: QUERY_KEYS.updatedFrom,
      to: QUERY_KEYS.updatedTo,
    }),
    completed: readDateFilter(params, {
      from: QUERY_KEYS.completedFrom,
      to: QUERY_KEYS.completedTo,
      empty: QUERY_KEYS.completedEmpty,
    }),
    authorIds: readList(params, QUERY_KEYS.authorIds),
    cycleIds: readList(params, QUERY_KEYS.cycleIds),
    estimates,
    hierarchy: readList(params, QUERY_KEYS.hierarchy).filter(
      (value): value is TaskHierarchyFilter =>
        HIERARCHY_VALUES.has(value as TaskHierarchyFilter)
    ),
    visibility: readList(params, QUERY_KEYS.visibility).filter(
      (value): value is TaskVisibilityFilter =>
        VISIBILITY_VALUES.has(value as TaskVisibilityFilter)
    ),
  };
}

function setList(params: URLSearchParams, key: string, values: readonly (string | number)[]) {
  params.delete(key);
  if (values.length > 0) params.set(key, unique(values.map(String)).join(","));
}

function setDateFilter(
  params: URLSearchParams,
  value: TaskDateFilter,
  keys: { from: string; to: string; empty?: string }
) {
  params.delete(keys.from);
  params.delete(keys.to);
  if (keys.empty) params.delete(keys.empty);
  if (validDate(value.from)) params.set(keys.from, value.from!);
  if (validDate(value.to)) params.set(keys.to, value.to!);
  if (keys.empty && value.includeEmpty) params.set(keys.empty, "1");
}

/** Replace only task-filter keys, preserving unrelated deep-link parameters. */
export function writeTaskFilters(
  filters: TaskFilters,
  source: URLSearchParams = new URLSearchParams()
): URLSearchParams {
  const params = new URLSearchParams(source.toString());
  for (const key of TASK_FILTER_QUERY_KEYS) params.delete(key);

  setList(params, QUERY_KEYS.assigneeIds, filters.assigneeIds);
  setList(params, QUERY_KEYS.ownerIds, filters.ownerIds);
  setList(params, QUERY_KEYS.executorAgentIds, filters.executorAgentIds);
  setList(params, QUERY_KEYS.requesterIds, filters.requesterIds);
  setList(params, QUERY_KEYS.projectIds, filters.projectIds);
  setDateFilter(params, filters.created, {
    from: QUERY_KEYS.createdFrom,
    to: QUERY_KEYS.createdTo,
  });
  setList(params, QUERY_KEYS.teamIds, filters.teamIds);
  setList(params, QUERY_KEYS.statuses, filters.statuses);
  setList(params, QUERY_KEYS.priorities, filters.priorities);
  setList(params, QUERY_KEYS.labelIds, filters.labelIds);
  setDateFilter(params, filters.due, {
    from: QUERY_KEYS.dueFrom,
    to: QUERY_KEYS.dueTo,
    empty: QUERY_KEYS.dueEmpty,
  });
  setDateFilter(params, filters.updated, {
    from: QUERY_KEYS.updatedFrom,
    to: QUERY_KEYS.updatedTo,
  });
  setDateFilter(params, filters.completed, {
    from: QUERY_KEYS.completedFrom,
    to: QUERY_KEYS.completedTo,
    empty: QUERY_KEYS.completedEmpty,
  });
  setList(params, QUERY_KEYS.authorIds, filters.authorIds);
  setList(params, QUERY_KEYS.cycleIds, filters.cycleIds);
  setList(params, QUERY_KEYS.estimates, filters.estimates);
  setList(params, QUERY_KEYS.hierarchy, filters.hierarchy);
  setList(params, QUERY_KEYS.visibility, filters.visibility);
  return params;
}

export function isTaskDateFilterActive(filter: TaskDateFilter): boolean {
  return !!filter.from || !!filter.to || filter.includeEmpty;
}

export function countActiveTaskFilters(filters: TaskFilters): number {
  return (Object.keys(filters) as TaskFilterFacet[]).reduce((count, facet) => {
    const value = filters[facet];
    const active = Array.isArray(value) ? value.length > 0 : isTaskDateFilterActive(value);
    return count + (active ? 1 : 0);
  }, 0);
}

export function clearTaskFilterFacet(filters: TaskFilters, facet: TaskFilterFacet): TaskFilters {
  const empty = createEmptyTaskFilters();
  return { ...filters, [facet]: empty[facet] } as TaskFilters;
}

function matchesNullableSelection(value: string | null, selected: string[]): boolean {
  if (selected.length === 0) return true;
  return value === null ? selected.includes(NO_FILTER_VALUE) : selected.includes(value);
}

function localDayStart(value: string): number {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).getTime();
}

function localDayEndExclusive(value: string): number {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day + 1).getTime();
}

function utcDayStart(value: string): number {
  const [year, month, day] = value.split("-").map(Number);
  return Date.UTC(year, month - 1, day);
}

function utcDayEndExclusive(value: string): number {
  const [year, month, day] = value.split("-").map(Number);
  return Date.UTC(year, month - 1, day + 1);
}

function matchesDate(
  value: number | null,
  filter: TaskDateFilter,
  calendar: "local" | "utc" = "local"
): boolean {
  if (!isTaskDateFilterActive(filter)) return true;
  if (value === null) return filter.includeEmpty;

  const hasRange = !!filter.from || !!filter.to;
  if (!hasRange) return false;
  const start = calendar === "utc" ? utcDayStart : localDayStart;
  const end = calendar === "utc" ? utcDayEndExclusive : localDayEndExclusive;
  if (filter.from && value < start(filter.from)) return false;
  if (filter.to && value >= end(filter.to)) return false;
  return true;
}

export function taskMatchesFilters(issue: Issue, filters: TaskFilters): boolean {
  if (!matchesNullableSelection(issue.assigneeId, filters.assigneeIds)) return false;
  if (!matchesNullableSelection(issue.ownerId ?? null, filters.ownerIds)) return false;
  if (!matchesNullableSelection(issue.executorAgentId ?? null, filters.executorAgentIds)) {
    return false;
  }
  if (!matchesNullableSelection(issue.requesterId, filters.requesterIds)) return false;
  if (!matchesNullableSelection(issue.projectId, filters.projectIds)) return false;
  if (!matchesDate(issue.createdAt, filters.created)) return false;
  if (filters.teamIds.length > 0 && !filters.teamIds.includes(issue.teamId)) return false;
  if (filters.statuses.length > 0 && !filters.statuses.includes(issue.status)) return false;
  if (filters.priorities.length > 0 && !filters.priorities.includes(issue.priority)) return false;
  if (filters.labelIds.length > 0) {
    const hasSelectedLabel = issue.labelIds.some((labelId) => filters.labelIds.includes(labelId));
    const matchesNoLabels =
      issue.labelIds.length === 0 && filters.labelIds.includes(NO_FILTER_VALUE);
    if (!hasSelectedLabel && !matchesNoLabels) return false;
  }
  // dueDate is stored as a UTC date-only timestamp (YYYY-MM-DDT00:00:00Z),
  // unlike created/updated/completed timestamps which represent real instants.
  if (!matchesDate(issue.dueDate, filters.due, "utc")) return false;
  if (!matchesDate(issue.updatedAt, filters.updated)) return false;
  if (!matchesDate(issue.completedAt, filters.completed)) return false;
  if (!matchesNullableSelection(issue.authorId, filters.authorIds)) return false;
  if (!matchesNullableSelection(issue.cycleId, filters.cycleIds)) return false;
  if (
    filters.estimates.length > 0 &&
    !filters.estimates.includes(issue.estimate === null ? NO_FILTER_VALUE : String(issue.estimate))
  ) {
    return false;
  }
  if (filters.hierarchy.length > 0) {
    const hierarchy: TaskHierarchyFilter = issue.parentId ? "subtask" : "top_level";
    if (!filters.hierarchy.includes(hierarchy)) return false;
  }
  if (filters.visibility.length > 0) {
    const visibility: TaskVisibilityFilter = issue.isPublic ? "public" : "private";
    if (!filters.visibility.includes(visibility)) return false;
  }
  return true;
}

export function applyTaskFilters(issues: Issue[], filters: TaskFilters): Issue[] {
  if (countActiveTaskFilters(filters) === 0) return issues;
  return issues.filter((issue) => taskMatchesFilters(issue, filters));
}

export function issueMatchesTaskSearch(
  issue: Issue,
  query: string,
  labelNames: ReadonlyMap<string, string>
): boolean {
  const term = query.trim().toLowerCase();
  if (!term) return true;
  return (
    issue.identifier.toLowerCase().includes(term) ||
    issue.title.toLowerCase().includes(term) ||
    issue.description.toLowerCase().includes(term) ||
    issue.labelIds.some((labelId) => labelNames.get(labelId)?.toLowerCase().includes(term))
  );
}

/** Keep provisioned humans visible before integration agents in filter pickers. */
export function orderTaskFilterUsers(users: User[]): User[] {
  return [...users].sort((a, b) => {
    if (!!a.isBot !== !!b.isBot) return a.isBot ? 1 : -1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

/**
 * Build the exact authenticated view URL. Unlike the old public-board helper,
 * this serializes the actual filters instead of guessing them from result rows.
 */
export function buildTaskViewUrl(
  currentUrl: string,
  filters: TaskFilters,
  options: {
    view: string | null;
    teamId: string | null;
    search: string;
    layout?: "list" | "board" | null;
    scopeUserId?: string | null;
    stripEntityDeepLinks?: boolean;
  }
): string {
  const url = new URL(currentUrl);
  url.search = writeTaskFilters(filters, url.searchParams).toString();
  url.searchParams.delete(TASK_NAV_QUERY_KEY);
  // Entity drawers and project command-center deep links are not part of a
  // copied task collection view. Normal in-app URL synchronization must keep
  // them intact so selecting a task cannot race against this helper.
  if (options.stripEntityDeepLinks) {
    url.searchParams.delete("issue");
    url.searchParams.delete("doc");
    url.searchParams.delete("note");
    url.searchParams.delete("project");
  }
  if (options.view) url.searchParams.set("task_view", options.view);
  else url.searchParams.delete("task_view");
  if (options.teamId) url.searchParams.set("task_team", options.teamId);
  else url.searchParams.delete("task_team");
  if (options.search.trim()) url.searchParams.set("task_search", options.search.trim());
  else url.searchParams.delete("task_search");
  if (options.layout) url.searchParams.set("task_layout", options.layout);
  else url.searchParams.delete("task_layout");
  if (options.view && options.scopeUserId) {
    url.searchParams.set("task_scope_user", options.scopeUserId);
  } else {
    url.searchParams.delete("task_scope_user");
  }
  if (options.view) url.hash = options.view;
  return url.toString();
}

/**
 * Remove state owned by task collection views without touching entity or
 * unrelated query parameters. Used when navigating away from task surfaces.
 */
export function clearTaskViewUrl(currentUrl: string): string {
  const url = new URL(currentUrl);
  for (const key of TASK_VIEW_QUERY_KEYS) url.searchParams.delete(key);
  return url.toString();
}

export type TaskViewLocation = {
  requestedView: string | null;
  queryView: string | null;
  explicitNavigation: boolean;
  serializedTaskView: boolean;
  filters: TaskFilters;
  search: string;
  layout: "list" | "board" | null;
  teamId: string | null;
  scopeUserId: string | null;
};

/**
 * Read task-view URL state with deterministic precedence. An explicit sidebar
 * marker wins, then the hash target, while task_view remains the auth-redirect
 * fallback. serializedTaskView distinguishes copied links from UI navigation.
 */
export function readTaskViewLocation(currentUrl: string): TaskViewLocation {
  const url = new URL(currentUrl);
  const navigationView = url.searchParams.get(TASK_NAV_QUERY_KEY);
  let hashView = url.hash.slice(1) || null;
  if (hashView) {
    try {
      hashView = decodeURIComponent(hashView);
    } catch {
      // Keep malformed user-edited hashes inert; Workspace validation will
      // reject them without making location hydration throw.
    }
  }
  const queryView = url.searchParams.get("task_view");
  const requestedView = navigationView || hashView || queryView;
  const rawLayout = url.searchParams.get("task_layout");
  return {
    requestedView,
    queryView,
    explicitNavigation: !!navigationView,
    serializedTaskView:
      !navigationView && !!queryView && (!hashView || hashView === queryView),
    filters: parseTaskFilters(url.searchParams),
    search: url.searchParams.get("task_search") ?? "",
    layout: rawLayout === "list" || rawLayout === "board" ? rawLayout : null,
    teamId: url.searchParams.get("task_team"),
    scopeUserId: url.searchParams.get("task_scope_user"),
  };
}
